/**
 * MockSTT - Mock Speech-to-Text engine for testing
 * 
 * Implements SpeechEngine interface for testing without actual speech recognition
 */

import { SpeechEngine, SpeechStates } from './SpeechEngine.js';
import { SpeechStateMachine } from './SpeechStateMachine.js';

export class MockSTT extends SpeechEngine {
    static isSupported() {
        return true; // Mock is always supported
    }

    constructor(options = {}) {
        super();
        
        this.options = {
            simulateDelay: options.simulateDelay || 1000,
            simulateErrors: options.simulateErrors || false,
            errorRate: options.errorRate || 0.1,
            ...options
        };
        
        this.stateMachine = new SpeechStateMachine({
            maxRetries: options.maxRetries || 3,
            baseDelay: options.baseDelay || 500,
            maxDelay: options.maxDelay || 5000
        });
        
        this.isSupported = true;
        this.partialBuffer = '';
        this.isManualStop = false;
        this.simulationTimeout = null;
        this.currentSimulation = null;
        
        this.setupStateMachine();
    }

    /**
     * Setup state machine callbacks
     */
    setupStateMachine() {
        this.stateMachine.setOnStateChange((stateChange) => {
            if (this.onStateChange) {
                this.onStateChange(stateChange);
            }
        });
    }

    /**
     * Start speech recognition simulation
     * @returns {Promise<boolean>} Success status
     */
    async start() {
        if (this.stateMachine.isListening()) {
            console.log('🎭 MockSTT: Already listening');
            return true;
        }

        try {
            this.isManualStop = false;
            this.partialBuffer = '';
            this.stateMachine.resetRetryCount();
            
            this.stateMachine.transitionTo(SpeechStates.ACQUIRING_MIC, { 
                reason: 'start_requested' 
            });
            
            // Simulate acquiring microphone
            setTimeout(() => {
                if (!this.isManualStop) {
                    this.stateMachine.transitionTo(SpeechStates.LISTENING, { 
                        reason: 'mic_acquired' 
                    });
                    this.startSimulation();
                }
            }, 100);
            
            console.log('🎭 MockSTT: Starting simulation...');
            return true;
        } catch (error) {
            console.error('🎭 MockSTT: Failed to start:', error);
            this.stateMachine.transitionTo(SpeechStates.ERROR, { 
                error: error.message, 
                reason: 'start_failed' 
            });
            return false;
        }
    }

    /**
     * Stop speech recognition simulation
     * @returns {Promise<boolean>} Success status
     */
    async stop() {
        try {
            this.isManualStop = true;
            this.stateMachine.clearRetryTimeout();
            this.clearSimulation();
            
            this.stateMachine.transitionTo(SpeechStates.IDLE, { 
                reason: 'manual_stop' 
            });
            
            console.log('🎭 MockSTT: Stopped simulation');
            return true;
        } catch (error) {
            console.error('🎭 MockSTT: Failed to stop:', error);
            return false;
        }
    }

    /**
     * Start simulation of speech recognition
     */
    startSimulation() {
        if (this.isManualStop) return;
        
        // Simulate partial results
        const partialPhrases = [
            'hey',
            'hey chef',
            'hey chef what',
            'hey chef what time',
            'hey chef what time is it'
        ];
        
        let phraseIndex = 0;
        
        const simulatePartial = () => {
            if (this.isManualStop || phraseIndex >= partialPhrases.length) {
                this.simulateFinal();
                return;
            }
            
            const phrase = partialPhrases[phraseIndex];
            this.partialBuffer = phrase;
            
            if (this.onPartial) {
                this.onPartial({
                    transcript: phrase,
                    confidence: 0.8 + Math.random() * 0.2,
                    isFinal: false
                });
            }
            
            phraseIndex++;
            
            this.simulationTimeout = setTimeout(simulatePartial, 500);
        };
        
        simulatePartial();
    }

    /**
     * Simulate final result
     */
    simulateFinal() {
        if (this.isManualStop) return;
        
        const finalPhrase = this.partialBuffer || 'hey chef what time is it';
        
        if (this.onFinal) {
            this.onFinal({
                transcript: finalPhrase,
                confidence: 0.9 + Math.random() * 0.1,
                isFinal: true
            });
        }
        
        // Simulate endpoint
        setTimeout(() => {
            if (!this.isManualStop) {
                this.stateMachine.transitionTo(SpeechStates.ENDPOINTED, { 
                    reason: 'simulation_complete' 
                });
            }
        }, 200);
    }

    /**
     * Clear simulation timeouts
     */
    clearSimulation() {
        if (this.simulationTimeout) {
            clearTimeout(this.simulationTimeout);
            this.simulationTimeout = null;
        }
    }

    /**
     * Check if currently listening
     * @returns {boolean} Listening status
     */
    isListening() {
        return this.stateMachine.isListening();
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getState() {
        return this.stateMachine.getCurrentState();
    }

    /**
     * Check if speech recognition is supported
     * @returns {boolean} Support status
     */
    isSupported() {
        return this.isSupported;
    }

    /**
     * Get mock browser info
     * @returns {Object} Mock browser info
     */
    getBrowserInfo() {
        return {
            hasSpeechRecognition: true,
            isChrome: true,
            isEdge: false,
            isSafari: false,
            userAgent: 'Mock Browser',
            isSupported: true,
            isMock: true
        };
    }

    /**
     * Simulate an error for testing
     * @param {string} errorType - Type of error to simulate
     */
    simulateError(errorType = 'network') {
        console.log(`🎭 MockSTT: Simulating error: ${errorType}`);
        
        this.stateMachine.transitionTo(SpeechStates.ERROR, { 
            error: errorType, 
            reason: 'simulated_error' 
        });
        
        if (this.onError) {
            this.onError({
                error: errorType,
                message: `Simulated ${errorType} error`
            });
        }
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.stop();
        this.stateMachine.destroy();
        this.clearSimulation();
        super.destroy();
    }
}
